import type { VariantProps } from "class-variance-authority"
import type { ComponentPropsWithoutRef, ReactNode } from "react"
import {
	createContext,
	forwardRef,
	useCallback,
	useContext,
	useState,
} from "react"
import { loadStripe } from "@stripe/stripe-js"
import { Link } from "@tanstack/react-router"

import type { PaidPlan } from "@productlane/lib/src/browser"
import type { buttonVariants } from "@productlane/ui"
import {
	cn,
	hasLegacyPlan,
	liveSlackConnectionIds,
	planHierarchy,
} from "@productlane/lib/src/browser"
import { AlertDialog, Button } from "@productlane/ui"

import { toast } from "@/components/toast"
import { env } from "@/env"
import { api } from "@/lib/api"
import { useSlackWorkspaceOptions } from "@/lib/slack-workspaces"
import { useTrial } from "@/lib/useTrial"
import { useSlackConnections, useZeroWorkspace } from "@/lib/zero"
import { slackDowngradeConfirm } from "../integrations/slack-plan-copy"
import { CancelSubscriptionDialog } from "./cancel-subscription-dialog"
import { PricingContext } from "./pricing-context"

const PUBLIC_STRIPE_API_KEY = env.VITE_STRIPE_PUBLISHABLE_KEY

const PlanHeaderContext = createContext({
	plan: "scale" as PaidPlan,
})

const PlanHeader = (props: { plan: PaidPlan; children?: ReactNode }) => {
	return (
		<div className="col-span-4 -mx-2 flex flex-col gap-2 bg-white px-2 lg:col-span-2 lg:mx-0 lg:bg-transparent lg:px-0 dark:bg-gray-900 dark:lg:bg-transparent">
			<PlanHeaderContext.Provider value={{ plan: props.plan }}>
				{props.children}
			</PlanHeaderContext.Provider>
		</div>
	)
}

const Name = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(
	({ className, ...props }, ref) => {
		return (
			<div
				ref={ref}
				className={cn(
					"text-lg font-medium text-gray-900 dark:text-gray-100",
					className,
				)}
				{...props}
			/>
		)
	},
)

const Price = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(
	({ className, ...props }, ref) => {
		const { subscriptionInterval, prices } = useContext(PricingContext)
		const { plan } = useContext(PlanHeaderContext)

		return (
			<div className={cn("mb-4", className)} ref={ref} {...props}>
				<div className="text-3xl font-semibold tracking-tight text-gray-900 lg:mr-2 dark:text-gray-100">
					{plan === "enterprise"
						? "Custom"
						: prices[plan]?.[subscriptionInterval]}
				</div>
				<div
					className={cn(
						"block whitespace-nowrap text-[11px] text-gray-500 lg:inline lg:text-xs dark:text-gray-400",
						plan === "enterprise" && "opacity-0",
					)}
				>
					per user/month
				</div>
			</div>
		)
	},
)

function onError(err: unknown) {
	if (err instanceof Error) {
		toast.error({ title: "Error", description: err.message })
	} else {
		toast.error({
			title: "An error occurred",
			description: "Please contact our support team at hello@productlane.com",
		})
	}
}

const CheckoutButton = ({ plan: planOverride }: { plan?: PaidPlan } = {}) => {
	const workspace = useZeroWorkspace()
	const {
		isTrial,
		daysLeft,
		hasPaymentMethod,
		hadRecentCancellation,
		cancelAtPeriodEnd,
	} = useTrial()
	const utils = api.useUtils()
	const { subscriptionInterval, activePlan } = useContext(PricingContext)
	const { plan: contextPlan } = useContext(PlanHeaderContext)
	const plan = planOverride ?? contextPlan
	const [showCancelDialog, setShowCancelDialog] = useState(false)
	const [showSlackPauseDialog, setShowSlackPauseDialog] = useState(false)

	const slackConnections = useSlackConnections()
	const slackWorkspaces = useSlackWorkspaceOptions()
	const liveNow = liveSlackConnectionIds(
		slackConnections,
		activePlan,
		workspace?.id,
	)
	const liveAfter = liveSlackConnectionIds(
		slackConnections,
		plan,
		workspace?.id,
	)
	const pausedByChange = slackWorkspaces
		.filter((option) => liveNow.has(option.id) && !liveAfter.has(option.id))
		.map((option) => option.name)

	// Subscribe to new plan (no trial - for Starter)
	const createSubscriptionMutation =
		api.workspace.createSubscription.useMutation({
			onError,
		})
	const subscribePlan = useCallback(async () => {
		const stripe = await loadStripe(PUBLIC_STRIPE_API_KEY)
		const sessionId = await createSubscriptionMutation
			.mutateAsync({ plan, interval: subscriptionInterval })
			.catch(() => undefined)
		if (!sessionId) return
		await stripe?.redirectToCheckout({
			sessionId,
		})
	}, [createSubscriptionMutation, plan, subscriptionInterval])

	// Add payment method
	const updatePaymentMutation = api.workspace.updatePayment.useMutation({
		onError,
	})
	const addPayment = useCallback(async () => {
		const stripe = await loadStripe(PUBLIC_STRIPE_API_KEY)
		let sessionId: Awaited<ReturnType<typeof updatePaymentMutation.mutateAsync>>
		try {
			sessionId = await updatePaymentMutation.mutateAsync()
		} catch {
			return
		}
		if (!sessionId) {
			toast.error({
				title: "Couldn't open the payment form",
				description:
					"Reload the page and try again, or write to hello@productlane.com.",
			})
			return
		}
		await stripe?.redirectToCheckout({
			sessionId,
		})
	}, [updatePaymentMutation])

	// Update plan
	const createUpdatePlanMutation = api.workspace.updatePlan.useMutation({
		onError,
	})
	const updatePlan = useCallback(async () => {
		let url: Awaited<ReturnType<typeof createUpdatePlanMutation.mutateAsync>>
		try {
			url = await createUpdatePlanMutation.mutateAsync({
				plan,
				interval: subscriptionInterval,
			})
		} catch {
			return
		}
		if (!url) {
			toast.error({
				title: "Couldn't open checkout",
				description:
					"Reload the page and try again, or write to hello@productlane.com.",
			})
			return
		}
		location.href = url
	}, [createUpdatePlanMutation, plan, subscriptionInterval])

	// Start trial subscription for upgrades
	const createTrialSubscriptionMutation =
		api.workspace.createTrialSubscription.useMutation({
			onError,
		})
	const startTrial = useCallback(async () => {
		const result = await createTrialSubscriptionMutation
			.mutateAsync({ plan, interval: subscriptionInterval })
			.catch(() => undefined)
		if (result) {
			if (result.startsWith("http")) {
				location.href = result
			} else {
				const stripe = await loadStripe(PUBLIC_STRIPE_API_KEY)
				await stripe?.redirectToCheckout({
					sessionId: result,
				})
			}
		}
	}, [createTrialSubscriptionMutation, plan, subscriptionInterval])

	// Resume cancelled subscription
	const resumeSubscriptionMutation =
		api.workspace.resumeSubscription.useMutation({
			onError,
			onSuccess: () => {
				void utils.workspace.trialInfo.invalidate()
				toast.success({ title: "Subscription resumed" })
			},
		})

	if (!workspace) {
		return <SignUpButton />
	}

	const isCurrentPlan = activePlan === plan
	const isLoading =
		createSubscriptionMutation.isLoading ||
		updatePaymentMutation.isLoading ||
		createUpdatePlanMutation.isLoading ||
		createTrialSubscriptionMutation.isLoading ||
		resumeSubscriptionMutation.isLoading

	const needsUpgrade = hasLegacyPlan(activePlan)

	// Button configuration
	const config = (():
		| {
				text: string
				variant: VariantProps<typeof buttonVariants>["variant"]
				onClick: () => Promise<string | void | null> | void
				disabled?: boolean
				className?: string
		  }
		| undefined => {
		const isDowngrade =
			planHierarchy.indexOf(plan) < planHierarchy.indexOf(activePlan) &&
			!isCurrentPlan

		const isUpgrade =
			planHierarchy.indexOf(plan) > planHierarchy.indexOf(activePlan) &&
			!isCurrentPlan

		if (isCurrentPlan && !hasPaymentMethod) {
			return {
				text: "Add payment details",
				variant: "default",
				onClick: addPayment,
			}
		}

		// A trial picking a lower plan pauses Slack workspaces just like a downgrade.
		const changePlan = () =>
			pausedByChange.length > 0
				? setShowSlackPauseDialog(true)
				: void updatePlan()

		if (!!daysLeft && isTrial && !isCurrentPlan) {
			return {
				text: "Select plan",
				variant: "outline",
				onClick: changePlan,
			}
		}

		if (isDowngrade) {
			return {
				text: "Downgrade plan",
				variant: "outline",
				onClick: changePlan,
			}
		}

		if (isCurrentPlan && cancelAtPeriodEnd) {
			return {
				text: "Resume subscription",
				variant: "default",
				onClick: () => void resumeSubscriptionMutation.mutateAsync(),
			}
		}

		if (isCurrentPlan) {
			return {
				text: "Cancel subscription",
				variant: "outline",
				onClick: () => setShowCancelDialog(true),
			}
		}

		if (isUpgrade) {
			const eligibleForTrial =
				(plan === "pro" || plan === "scale") && !hadRecentCancellation

			return {
				text: eligibleForTrial ? "Start 7-day trial" : "Upgrade plan",
				variant: "default",
				onClick: eligibleForTrial ? startTrial : subscribePlan,
			}
		}
	})()

	return (
		<>
			{!!config && (
				<Button
					variant={config.variant}
					onClick={config.onClick}
					isLoading={isLoading}
					disabled={config.disabled || (needsUpgrade && activePlan !== "free")}
					className={config.className}
				>
					{config.text}
				</Button>
			)}
			<CancelSubscriptionDialog
				isOpen={showCancelDialog}
				onClose={() => setShowCancelDialog(false)}
			/>
			{pausedByChange.length > 0 && (
				<AlertDialog
					variant="negative"
					isOpen={showSlackPauseDialog}
					onOpenChange={setShowSlackPauseDialog}
					{...slackDowngradeConfirm(plan, pausedByChange)}
					onCancel={() => setShowSlackPauseDialog(false)}
					onConfirm={async () => {
						setShowSlackPauseDialog(false)
						await updatePlan()
					}}
				/>
			)}
		</>
	)
}

function SignUpButton() {
	return (
		<Link to="/login">
			<Button className="w-full">Sign up</Button>
		</Link>
	)
}

export default Object.assign(PlanHeader, {
	Name,
	Price,
	CheckoutButton,
	SignUpButton,
})
